import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLIENT_ROLE_OPTIONS, DAYS } from "@/lib/workspace";
import { toast } from "sonner";

const CONFIDENTIAL = [["pay", "Pay details"], ["medical", "Medical / workplace adjustments"], ["notes", "Other confidential notes"]];

export function EmployeeDialog({ orgId, profile, members, isPeopleAdmin, onClose, onSaved }) {
  const isNew = !profile;
  const [f, setF] = useState({
    name: profile?.name || "", email: profile?.email || "", job_title: profile?.job_title || "", department: profile?.department || "",
    role: profile?.role || "client_team_member", manager_user_id: profile?.manager_user_id || "none",
    employment_type: profile?.employment_type || "full_time", start_date: profile?.start_date || "",
    work_pattern: profile?.work_pattern || { mon: 7.5, tue: 7.5, wed: 7.5, thu: 7.5, fri: 7.5 },
    confidential: profile?.confidential || {}, status: profile?.status,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const managers = members.filter((m) => ["client_admin", "client_manager"].includes(m.role) && m.id !== profile?.user_id);

  const save = async () => {
    if (!f.name.trim() || !f.email.trim()) return toast.error("Add a name and email");
    const pattern = Object.fromEntries(Object.entries(f.work_pattern).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)]));
    const body = { ...f, manager_user_id: f.manager_user_id === "none" ? null : f.manager_user_id, work_pattern: pattern, start_date: f.start_date || null };
    if (!isPeopleAdmin) delete body.confidential;
    if (isNew) delete body.status;
    if (!isNew && profile.user_id) delete body.email;
    setSaving(true);
    try {
      if (isNew) await api.post(`/workspace/employees?org_id=${orgId}`, body);
      else await api.patch(`/workspace/employees/${profile.id}`, body);
      toast.success(isNew ? "Profile prepared — you can invite them now" : "Profile updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Prepare an employee profile" : profile.name}</DialogTitle>
          <DialogDescription>Profiles can be set up before someone joins. Their invitation links to this profile.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3.5 sm:grid-cols-2">
          <div><Label>Full name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="emp-name" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} disabled={!isNew && !!profile.user_id} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="emp-email" /></div>
          <div><Label>Job title</Label><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} className="mt-1" data-testid="emp-title" /></div>
          <div><Label>Department / team</Label><Input value={f.department} onChange={(e) => set("department", e.target.value)} className="mt-1" data-testid="emp-dept" /></div>
          <div><Label>Access</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="mt-1" data-testid="emp-role"><SelectValue /></SelectTrigger>
              <SelectContent>{CLIENT_ROLE_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Manager</Label>
            <Select value={f.manager_user_id} onValueChange={(v) => set("manager_user_id", v)}>
              <SelectTrigger className="mt-1" data-testid="emp-manager"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">No manager</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Employment type</Label>
            <Select value={f.employment_type} onValueChange={(v) => set("employment_type", v)}>
              <SelectTrigger className="mt-1" data-testid="emp-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="full_time">Full-time</SelectItem><SelectItem value="part_time">Part-time</SelectItem><SelectItem value="contract">Contract</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Start date</Label><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="mt-1" data-testid="emp-start" /></div>
          {!isNew && (
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1" data-testid="emp-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["prepared", "invited", "active", "left"].map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div>
          <Label>Work pattern (hours per day)</Label>
          <div className="mt-1 grid grid-cols-7 gap-1.5">
            {DAYS.map(([k, l]) => (
              <div key={k}><div className="mb-0.5 text-center text-[11px] text-[#1E2D44]/50">{l}</div>
                <Input type="number" min="0" max="24" step="0.5" className="h-9 px-1 text-center" value={f.work_pattern[k] ?? ""} data-testid={`emp-wp-${k}`}
                  onChange={(e) => set("work_pattern", { ...f.work_pattern, [k]: e.target.value === "" ? undefined : e.target.value })} />
              </div>
            ))}
          </div>
        </div>
        {isPeopleAdmin && (
          <div className="rounded-xl border border-[#C95555]/25 bg-[#C95555]/[0.03] p-4" data-testid="emp-confidential">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[#0B1729]"><Icons.Lock size={14} className="text-[#C95555]" /> Confidential — people administrators only</div>
            <p className="mb-3 text-xs text-[#1E2D44]/55">Optional. Never shown to managers or Amden advisers, and never used by Ask Amden.</p>
            <div className="space-y-2.5">
              {CONFIDENTIAL.map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label>
                  <Textarea rows={2} className="mt-1 bg-white" value={f.confidential[k] || ""} data-testid={`emp-conf-${k}`}
                    onChange={(e) => set("confidential", { ...f.confidential, [k]: e.target.value })} />
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-employee-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : isNew ? "Prepare profile" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
